import React from 'react';
import { Building2, Star, ArrowRight, AlertTriangle } from 'lucide-react';
import { formatDate, isNew } from '../../services/samApi';

interface ContractListItemProps {
    opp: any;
    isSaved: boolean;
    onToggleSave: (opp: any, e?: React.MouseEvent) => void;
    onSelect: (opp: any) => void;
    selectedContractor: string;
}

export default function ContractListItem({
    opp,
    isSaved,
    onToggleSave,
    onSelect,
    selectedContractor
}: ContractListItemProps) {
    const agencyName = opp.agency || opp.fullParentPathName?.split('.')[0] || 'Unknown';
    const score = opp.fitScore || 0;
    const isHighFit = score >= 90;

    // Relative Time Logic
    const daysLeft = opp.responseDeadline ? Math.ceil((new Date(opp.responseDeadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24)) : -1;
    const deadlineColor = daysLeft <= 2 ? 'text-red-400' : daysLeft <= 7 ? 'text-amber-400' : 'text-gray-400';

    return (
        <div
            onClick={() => onSelect(opp)}
            className="group relative bg-[#0A0A0A] hover:bg-[#111111] border border-white/5 hover:border-blue-900/30 rounded-lg p-4 md:p-0 md:h-20 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center transition-all cursor-pointer overflow-hidden"
        >
            {/* Hover Highlight Line */}
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Score Ring */}
            <div className="md:col-span-1 flex flex-col items-center justify-center order-1 md:order-1 mb-2 md:mb-0 gap-1">
                <div className="flex items-center gap-2 md:hidden">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${isNew(opp.postedDate) ? 'bg-blue-900/40 text-blue-400' : 'bg-gray-800 text-gray-400'}`}>
                        {isNew(opp.postedDate) ? 'NEW' : opp.type}
                    </span>
                </div>
                <div className="relative w-10 h-10 md:w-10 md:h-10 flex items-center justify-center">
                    <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="3" className="text-gray-800" fill="none" />
                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="3" className={`${isHighFit ? 'text-amber-500' : 'text-blue-600'}`}
                            strokeDasharray="100" strokeDashoffset={100 - (score || 0)} strokeLinecap="round" fill="none"
                        />
                    </svg>
                    <span className={`text-[10px] font-bold ${isHighFit ? 'text-amber-500' : 'text-white'}`}>{score}</span>
                </div>
                {/* Opportunity Score Badge */}
                {(opp.opportunityScore || 0) > 0 && (
                    <div className="hidden md:block text-[9px] font-mono text-emerald-400 bg-emerald-900/30 px-1.5 py-0.5 rounded border border-emerald-900/50" title="Strategic Opportunity Score">
                        +{opp.opportunityScore}
                    </div>
                )}
            </div>

            {/* Opportunity Details */}
            <div className="md:col-span-6 order-2 md:order-2 space-y-2">
                <h3 className="text-sm md:text-sm font-medium text-gray-200 group-hover:text-blue-400 transition-colors line-clamp-2 leading-relaxed">
                    {opp.title}
                </h3>
                <div className="flex items-center flex-wrap gap-2 text-[10px] text-gray-500">
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {agencyName}</span>
                    <span className="hidden md:inline">•</span>
                    <span className="md:hidden flex items-center gap-1">{formatDate(opp.postedDate)}</span>
                    {opp.typeOfSetAside && <span className="text-amber-600/80 border border-amber-900/30 px-1 rounded hidden md:inline">{opp.typeOfSetAside}</span>}

                    {(opp.winProbability) && (
                        <span className={`px-1.5 py-0.5 rounded border ${opp.winProbability === 'High' ? 'bg-green-900/20 text-green-400 border-green-900/30' :
                            opp.winProbability === 'Medium' ? 'bg-amber-900/20 text-amber-400 border-amber-900/30' :
                                'bg-red-900/20 text-red-400 border-red-900/30'
                            }`}>
                            Win: {opp.winProbability}
                        </span>
                    )}
                </div>

                {/* Heuristic Insight Banner */}
                {(opp.insights && opp.insights.length > 0) && (
                    <div className="mt-2.5 w-full flex flex-col gap-1 border-l-2 border-l-blue-500/50 bg-blue-900/10 rounded-r px-2 py-1.5">
                        {opp.insights.map((insight: string, idx: number) => {
                            const isRisk = insight.includes('⚠️');
                            const isMoney = insight.includes('💰');
                            const isUrgent = insight.includes('🔥');
                            const isCheck = insight.includes('✅');
                            const color = isRisk ? 'text-rose-400' : isMoney ? 'text-emerald-400' : isUrgent ? 'text-amber-400' : 'text-blue-200';

                            return (
                                <span key={idx} className={`text-[10px] font-medium flex items-start gap-1.5 ${color} leading-snug`}>
                                    <span className="opacity-80 mt-0.5">{isRisk || isMoney || isUrgent || isCheck ? '' : '💡'}</span>
                                    {insight}
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* AI Risks Summary */}
                {opp.risks && opp.risks.length > 0 && (
                    <div className="hidden md:flex gap-2 mt-1">
                        {opp.risks.slice(0, 2).map((risk: string, idx: number) => (
                            <span key={idx} className="text-[9px] text-rose-400/80 bg-rose-950/30 px-1.5 py-0.5 rounded border border-rose-900/30 flex items-center gap-1">
                                <AlertTriangle className="w-2.5 h-2.5" /> {risk}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Deadline */}
            <div className="md:col-span-2 order-3 md:order-3 flex items-center justify-between md:block mt-2 md:mt-0 pt-2 md:pt-0 border-t border-white/5 md:border-none">
                <span className="text-xs text-gray-500 md:hidden">Due:</span>
                <div className="text-right md:text-left">
                    <div className={`font-mono text-xs md:text-sm ${daysLeft > 0 ? deadlineColor : daysLeft === 0 ? 'text-amber-500' : 'text-gray-500'}`}>
                        {opp.responseDeadline
                            ? (daysLeft > 0 ? `${daysLeft}d left` : daysLeft === 0 ? 'Due Today' : 'Closed')
                            : 'Open / N/A'}
                    </div>
                    <div className="text-[10px] text-gray-600 hidden md:block">
                        {opp.responseDeadline ? new Date(opp.responseDeadline).toLocaleDateString() : 'N/A'}
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="md:col-span-3 order-4 md:order-4 flex justify-end items-center gap-2 pr-0 md:pr-4">
                <button
                    onClick={(e) => onToggleSave(opp, e)}
                    disabled={!selectedContractor}
                    className={`p-2 rounded hover:bg-white/10 transition-colors ${isSaved ? 'text-amber-400' : 'text-gray-600'}`}
                >
                    <Star className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                </button>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-xs rounded transition-all shadow-lg shadow-blue-900/50">
                    View <ArrowRight className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}
